// Contrail V3.12.0 — keep the live map current after the phone/computer sleeps or the network drops.
// Before: positions came only from a Realtime subscription. When a phone app sat in the background (or the laptop slept),
// the socket died and changes made meanwhile were never fetched, so every vehicle drifted to "Offline" until the app was
// closed and reopened. Now:
//   • coming back to the page (after > 15 s hidden), the network coming back, or a page restored from the back/forward
//     cache → renew the sign-in if it is about to expire, reload all positions and re-subscribe;
//   • a channel error / timeout / close → re-subscribe after 3 s (and reload once it is live again);
//   • while not live, positions are reloaded every 60 s as a safety net.
import { useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

export type FeedState = "connecting" | "live" | "reconnecting";

const HIDDEN_RESYNC_MS = 15_000;
const RETRY_MS = 3_000;
const POLL_MS = 60_000;

/** Make sure the access token is valid before re-subscribing (the token can expire while the app sleeps). */
export async function freshAuth() {
  try {
    let { data } = await supabase.auth.getSession();
    const exp = (data.session?.expires_at ?? 0) * 1000;
    if (data.session && exp - Date.now() < 120_000) data = (await supabase.auth.refreshSession()).data as typeof data;
    const token = data.session?.access_token;
    if (token) (supabase as any).realtime?.setAuth?.(token);
  } catch { /* the reload below will show any real sign-in problem */ }
}

/**
 * Subscribe to postgres_changes on `table` and keep it healthy.
 * `onRow(newRow)` gets every change; `reload()` re-fetches the full list (called on resume / reconnect / while down).
 */
export function useLiveFeed(name: string, table: string, onRow: (row: any) => void, reload: () => unknown): FeedState {
  const [state, setState] = useState<FeedState>("connecting");
  const [gen, setGen] = useState(0);
  const onRowRef = useRef(onRow); onRowRef.current = onRow;
  const reloadRef = useRef(reload); reloadRef.current = reload;

  // the subscription (re-created whenever gen changes)
  useEffect(() => {
    let alive = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const ch = supabase
      .channel(`${name}_${gen}`)
      .on("postgres_changes", { event: "*", schema: "public", table }, (p) => onRowRef.current(p.new))
      .subscribe((st) => {
        if (!alive) return;
        if (st === "SUBSCRIBED") {
          setState("live");
          if (gen > 0) reloadRef.current();          // catch up on anything missed while it was down
        } else if (st === "CHANNEL_ERROR" || st === "TIMED_OUT" || st === "CLOSED") {
          setState("reconnecting");
          clearTimeout(retry);
          retry = setTimeout(() => { if (alive) freshAuth().then(() => alive && setGen((g) => g + 1)); }, RETRY_MS);
        }
      });
    return () => { alive = false; clearTimeout(retry); supabase.removeChannel(ch); };
  }, [name, table, gen]);

  // resume after sleep / network back
  useEffect(() => {
    let hiddenAt = document.visibilityState === "hidden" ? Date.now() : 0;
    let busy = false;
    const resync = async () => {
      if (busy) return;
      busy = true;
      try {
        setState((s) => (s === "live" ? "reconnecting" : s));
        await freshAuth();
        await reloadRef.current();
        setGen((g) => g + 1);
      } finally { busy = false; }
    };
    const vis = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > HIDDEN_RESYNC_MS) resync();
      hiddenAt = 0;
    };
    const online = () => resync();
    const show = (e: PageTransitionEvent) => { if (e.persisted) resync(); };
    document.addEventListener("visibilitychange", vis);
    window.addEventListener("online", online);
    window.addEventListener("pageshow", show);
    return () => {
      document.removeEventListener("visibilitychange", vis);
      window.removeEventListener("online", online);
      window.removeEventListener("pageshow", show);
    };
  }, []);

  // safety net while the live link is down
  useEffect(() => {
    if (state === "live") return;
    const t = setInterval(() => { if (document.visibilityState === "visible") reloadRef.current(); }, POLL_MS);
    return () => clearInterval(t);
  }, [state]);

  return state;
}
